// ── Accommodation context derivation ──
// Shared by the week and day views: resolves the sparse sleep map into the
// per-date wake/sleep POIs, palettes, and night-run position a column needs.

import type { POI, Schedule } from "@poi-planner/shared";
import { addDaysToDate } from "../../utils/zonedTime";
import { getAccommodationPalette } from "../../utils/colors";
import type { DayAccommodationContext } from "./types";

/** Consecutive-night run for the sleep map, e.g. "Night 2/3". */
export function nightCountFor(
  sleep: Schedule["sleep"],
  date: string,
): { current: number; total: number } | null {
  const poiId = sleep[date];
  if (!poiId) return null;

  let first = date;
  while (sleep[addDaysToDate(first, -1)] === poiId) {
    first = addDaysToDate(first, -1);
  }
  let last = date;
  while (sleep[addDaysToDate(last, 1)] === poiId) {
    last = addDaysToDate(last, 1);
  }

  let current = 1;
  let total = 1;
  for (let day = first; day !== last; day = addDaysToDate(day, 1)) {
    total++;
    if (day < date) current++;
  }
  return { current, total };
}

export function buildAccommodationContext(
  date: string,
  schedule: Schedule,
  getAccommodation: (id: string) => POI | undefined,
): DayAccommodationContext {
  const sleepPoiId = schedule.sleep[date] ?? null;
  const wakePoiId = schedule.sleep[addDaysToDate(date, -1)] ?? null;
  const sleepPoi = sleepPoiId ? (getAccommodation(sleepPoiId) ?? null) : null;
  const wakePoi = wakePoiId ? (getAccommodation(wakePoiId) ?? null) : null;
  const sleepConfig = sleepPoiId
    ? schedule.accommodationConfigs[sleepPoiId]
    : undefined;
  const wakeConfig = wakePoiId
    ? schedule.accommodationConfigs[wakePoiId]
    : undefined;

  return {
    sleepPoi,
    sleepConfig,
    sleepPalette: sleepPoi ? getAccommodationPalette(sleepConfig) : null,
    wakePoi,
    wakeConfig,
    wakePalette: wakePoi ? getAccommodationPalette(wakeConfig) : null,
    isSwap: !!sleepPoi && !!wakePoi && sleepPoi.id !== wakePoi.id,
    nightCount: nightCountFor(schedule.sleep, date),
  };
}
